import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { CloseSVG } from '../../utils/svgs';

export interface ILightboxItem {
	url: string;
	type: 'image' | 'video';
}

interface ILightboxProps {
	/** Plain strings are treated as images (back-compat with existing callers). */
	images: (string | ILightboxItem)[];
	startIndex?: number;
	label?: string;
	onClose: () => void;
}

/**
 * Dependency-free, focus-trapped media lightbox for result sheets / report
 * media (images + video). Built in-tab rather than nesting a RightSection
 * inside the drawer. Esc closes; Left/Right step through multiple items.
 */
const Lightbox = ({ images, startIndex = 0, label = 'Image', onClose }: ILightboxProps) => {
	const [index, setIndex] = useState(startIndex);
	const closeRef = useRef<HTMLButtonElement>(null);
	const items: ILightboxItem[] = images.map((item) => (typeof item === 'string' ? { url: item, type: 'image' } : item));

	useEffect(() => {
		closeRef.current?.focus();
	}, []);

	const step = (delta: number) => setIndex((i) => (i + delta + images.length) % images.length);

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			onClose();
		} else if (e.key === 'ArrowRight' && images.length > 1) {
			e.preventDefault();
			step(1);
		} else if (e.key === 'ArrowLeft' && images.length > 1) {
			e.preventDefault();
			step(-1);
		}
	};

	return (
		<div
			className='ca-lightbox'
			role='dialog'
			aria-modal='true'
			aria-label={`${label} viewer`}
			onKeyDown={onKeyDown}
		>
			<div
				className='ca-lightbox__backdrop'
				onClick={onClose}
			/>
			<div className='ca-lightbox__panel'>
				<div className='ca-lightbox__bar'>
					<span className='ca-lightbox__count'>
						{label} {images.length > 1 ? `${index + 1} / ${images.length}` : ''}
					</span>
					<button
						ref={closeRef}
						type='button'
						className='ca-lightbox__close'
						onClick={onClose}
						aria-label='Close viewer'
					>
						<CloseSVG
							width='18'
							height='18'
							color='currentColor'
						/>
					</button>
				</div>
				<div className='ca-lightbox__stage'>
					{images.length > 1 ? (
						<button
							type='button'
							className='ca-lightbox__nav ca-lightbox__nav--prev'
							onClick={() => step(-1)}
							aria-label='Previous image'
						>
							‹
						</button>
					) : null}
					{items[index]?.type === 'video' ? (
						// eslint-disable-next-line jsx-a11y/media-has-caption -- field-recorded evidence has no caption track
						<video
							key={items[index].url}
							src={items[index].url}
							className='ca-lightbox__img'
							controls
							playsInline
							aria-label={`${label} ${index + 1} of ${items.length}`}
						/>
					) : (
						<img
							src={items[index]?.url}
							alt={`${label} ${index + 1} of ${items.length}`}
							className='ca-lightbox__img'
						/>
					)}
					{images.length > 1 ? (
						<button
							type='button'
							className='ca-lightbox__nav ca-lightbox__nav--next'
							onClick={() => step(1)}
							aria-label='Next image'
						>
							›
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default Lightbox;
